import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { 
  Grid, 
  TextField, 
  RadioGroup, 
  FormControlLabel, 
  Radio, 
  FormLabel, 
  FormControl, 
  CardHeader, 
  CardContent, 
  Divider, 
  Card, 
  Tooltip, 
  IconButton, 
  Container, 
  Button, 
  LinearProgress, 
  Box, 
  Chip, 
  Typography,
  FormHelperText,
  Stack
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import {
  fetchInvoiceSettings,
  resetInvoiceNotifications,
  updateInvoiceSettings
} from './invoiceSlice';
import Notification from '../../../pages/General/Notification';

// Global styling matrix configuration mappings for premium enterprise custom text input boxes
const enterpriseInvoiceFormBoxStyles = (hasError) => ({
  display: 'flex', 
  flexWrap: 'wrap', 
  alignItems: 'center', 
  border: '1px solid',
  borderColor: hasError ? '#EF4444' : '#E2E8F0', 
  padding: '6px 10px', 
  borderRadius: '10px',
  backgroundColor: '#FFFFFF',
  transition: 'all 0.2s ease-in-out',
  gap: '4px',
  '&:hover': {
    borderColor: hasError ? '#EF4444' : '#CBD5E1',
    boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
  },
  '&:focus-within': {
    borderColor: hasError ? '#EF4444' : '#1976d2',
    boxShadow: hasError ? 'none' : '0 0 0 3px rgba(25, 118, 210, 0.08)'
  }
});

const InvoiceSettingsPage = () => {
  const dispatch = useDispatch();
  const profile = useSelector(state => state.auth.profile);
  const invoice = useSelector(state => state.invoice.invoiceSettings);
  const [invoiceType, setInvoiceType] = React.useState('standard');
  const [invoiceEmails, setInvoiceEmails] = React.useState([]);
  const [invoiceCCEmails, setInvoiceCCEmails] = React.useState([]);
  const [inputValue, setInputValue] = React.useState('');
  const [inputCCValue, setInputCCValue] = React.useState('');
  const [errors, setErrors] = React.useState({});

  const handleInvoiceTypeChange = (event) => {
    setInvoiceType(event.target.value);
  };

  const handleKeyDown = (event, type) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      const email = event.target.value.trim();
      if (email && validateEmail(email)) {
        if (type === 'invoice' && !invoiceEmails.includes(email) && invoiceEmails.length < 10) {
          setInvoiceEmails([...invoiceEmails, email]);
          setInputValue('');
          if (errors.invoiceEmail) {
            setErrors((prevErrors) => ({ ...prevErrors, invoiceEmail: null }));
          }
        } else if (type === 'invoiceCC' && !invoiceCCEmails.includes(email) && invoiceCCEmails.length < 10) {
          setInvoiceCCEmails([...invoiceCCEmails, email]);
          setInputCCValue('');
          if (errors.invoiceCCEmail) {
            setErrors((prevErrors) => ({ ...prevErrors, invoiceCCEmail: null }));
          }
        }
      }
    }
  };

  const handleDelete = (email, type) => {
    if (type === 'invoice') {
      setInvoiceEmails(invoiceEmails.filter((e) => e !== email));
    } else {
      setInvoiceCCEmails(invoiceCCEmails.filter((e) => e !== email));
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
    return emailRegex.test(email);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const validationErrors = {};
    if (invoiceEmails.length === 0) {
      validationErrors.invoiceEmail = 'Invoice Email is required';
    }

    if (Object.values(validationErrors).some((error) => error)) {
      setErrors(validationErrors);
    } else {
      const formData = {
        invoice_email: invoiceEmails.join(','),
        invoice_cc_email: invoiceCCEmails.join(','),
        invoice_csv_type: invoiceType
      };
      dispatch(updateInvoiceSettings(profile && profile.id, formData))
    }
  };

  useEffect(() => {
    if (invoice && invoice.data) {
      setInvoiceEmails(invoice.data.invoice_email ? invoice.data.invoice_email.split(',') : []);
      setInvoiceCCEmails(invoice.data.invoice_cc_email ? invoice.data.invoice_cc_email.split(',') : []);
      setInvoiceType(invoice.data.invoice_csv_type || 'standard');
    }
  }, [invoice])

  useEffect(() => {
    dispatch(fetchInvoiceSettings())
  }, [])

  return (
    <Box sx={{ width: '100%', boxSizing: 'border-box', p: 2.5 }}>
      {(invoice && invoice.notification && invoice.notification.messageContent != '') &&
        <Notification
          is_open={invoice.notification.show}
          messageType={invoice.notification.messageType}
          messages={invoice.notification.messageContent}
          handleClose={() => dispatch(resetInvoiceNotifications())}
        />}
        
      {/* Core Framing Master Viewport Card Surface */}
      <Card 
        sx={{ 
          width: '100%', 
          backgroundColor: '#FFFFFF',
          border: '1px solid #E2E8F0',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
          overflow: 'hidden'
        }} 
        className="sc--cardhead--details"
      >
        <CardHeader 
          title={
            <Typography variant="h6" component="h1" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '16px', m: 0 }}>
              Invoice Settings
            </Typography>
          } 
          action={
            <Tooltip title="Back to Billing" placement="top">
              <Link to="/settings/billing" style={{ textDecoration: 'none' }}>
                <IconButton 
                  sx={{ 
                    color: '#64748B', 
                    borderRadius: '8px', 
                    border: '1px solid #E2E8F0',
                    p: 1,
                    transition: 'all 0.15s ease-in-out',
                    '&:hover': { backgroundColor: '#F1F5F9', color: '#1E293B', borderColor: '#CBD5E1' }
                  }}
                >
                  <ArrowBackIosNewIcon sx={{ fontSize: '14px' }} />
                </IconButton>
              </Link>
            </Tooltip>
          }
          sx={{ p: 2.5, backgroundColor: '#FFFFFF' }}
        />
        
        {invoice && invoice.loading === true && (
          <LinearProgress sx={{ height: '3px', backgroundColor: '#E2E8F0', '& .MuiLinearProgress-bar': { backgroundColor: '#1976d2' } }} />
        )}
        
        <Divider sx={{ borderColor: '#E2E8F0' }} />
        
        <CardContent sx={{ p: 4, boxSizing: 'border-box' }}>
          <Container maxWidth="sm" disableGutters>
            <form onSubmit={handleSubmit} noValidate>
              <Grid container spacing={3.5}>
                
                {/* 1. Primary Invoice Recipients Multi-Tag Chip Input */}
                <Grid item xs={12}>
                  <FormLabel 
                    component="legend"
                    sx={{ 
                      fontSize: '12.5px', 
                      fontWeight: 700, 
                      color: '#64748B', 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.5px',
                      mb: 1.2,
                      '&.Mui-focused': { color: '#64748B' }
                    }}
                  >
                    Invoice Emails*
                  </FormLabel>
                  <Box sx={enterpriseInvoiceFormBoxStyles(!!errors.invoiceEmail)}>
                    {invoiceEmails.map((email, index) => (
                      <Chip 
                        key={index} 
                        label={email} 
                        onDelete={() => handleDelete(email, 'invoice')} 
                        size="small"
                        sx={{ 
                          margin: '2px',
                          borderRadius: '6px',
                          fontWeight: 600,
                          fontSize: '12px',
                          backgroundColor: '#F1F5F9',
                          color: '#334155',
                          border: '1px solid #E2E8F0',
                          '& .MuiChip-deleteIcon': { color: '#94A3B8', '&:hover': { color: '#64748B' } }
                        }} 
                      />
                    ))}
                    <TextField
                      value={inputValue}
                      onChange={(event) => {
                        setInputValue(event.target.value);
                        if (event.target.value && errors.invoiceEmail) {
                          setErrors((prevErrors) => ({ ...prevErrors, invoiceEmail: null }));
                        } else if (event.target.value === '' && invoiceEmails.length > 0 && errors.invoiceEmail) {
                          setErrors((prevErrors) => ({ ...prevErrors, invoiceEmail: null }));
                        }
                      }}
                      onKeyDown={(event) => handleKeyDown(event, 'invoice')}
                      placeholder={invoiceEmails.length === 0 ? "Enter email addresses..." : ""}
                      variant="standard"
                      InputProps={{ disableUnderline: true }}
                      sx={{ 
                        flex: 1, 
                        minWidth: '160px',
                        '& .MuiInputBase-input': { 
                          padding: '6px 8px', 
                          fontSize: '13.5px',
                          color: '#1E293B',
                          fontWeight: 500
                        } 
                      }}
                    />
                  </Box>
                  {errors.invoiceEmail && (
                    <FormHelperText sx={{ color: '#EF4444', mx: 0.5, mt: 0.75, fontWeight: 500 }}>
                      {errors.invoiceEmail}
                    </FormHelperText>
                  )}
                </Grid>

                {/* 2. Secondary Invoice CC Recipients Multi-Tag Chip Input */}
                <Grid item xs={12}>
                  <FormLabel 
                    component="legend"
                    sx={{ 
                      fontSize: '12.5px', 
                      fontWeight: 700, 
                      color: '#64748B', 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.5px',
                      mb: 1.2,
                      '&.Mui-focused': { color: '#64748B' }
                    }}
                  >
                    Invoice CC Emails
                  </FormLabel>
                  <Box sx={enterpriseInvoiceFormBoxStyles(false)}>
                    {invoiceCCEmails.map((email, index) => (
                      <Chip 
                        key={index} 
                        label={email} 
                        onDelete={() => handleDelete(email, 'invoiceCC')} 
                        size="small"
                        sx={{ 
                          margin: '2px',
                          borderRadius: '6px',
                          fontWeight: 600,
                          fontSize: '12px',
                          backgroundColor: '#F1F5F9',
                          color: '#334155',
                          border: '1px solid #E2E8F0',
                          '& .MuiChip-deleteIcon': { color: '#94A3B8', '&:hover': { color: '#64748B' } }
                        }} 
                      />
                    ))}
                    <TextField
                      value={inputCCValue}
                      onChange={(event) => {
                        setInputCCValue(event.target.value);
                      }}
                      onKeyDown={(event) => handleKeyDown(event, 'invoiceCC')}
                      placeholder={invoiceCCEmails.length === 0 ? "Enter email addresses..." : ""}
                      variant="standard"
                      InputProps={{ disableUnderline: true }}
                      sx={{ 
                        flex: 1, 
                        minWidth: '160px',
                        '& .MuiInputBase-input': { 
                          padding: '6px 8px', 
                          fontSize: '13.5px',
                          color: '#1E293B',
                          fontWeight: 500
                        } 
                      }}
                    />
                  </Box>
                </Grid>

                {/* 3. Preferred Report Export Template Type Selector Module */}
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel 
                      component="legend"
                      sx={{ 
                        fontSize: '12.5px', 
                        fontWeight: 700, 
                        color: '#64748B', 
                        textTransform: 'uppercase', 
                        letterSpacing: '0.5px',
                        mb: 1.5,
                        '&.Mui-focused': { color: '#64748B' }
                      }}
                    >
                      Prefer your invoice type
                    </FormLabel>
                    <Box sx={{ p: 2, backgroundColor: '#F8FAFC', borderRadius: '12px', border: '1px solid #E2E8F0', minWidth: { sm: '320px' } }}>
                      <RadioGroup
                        name="invoiceType"
                        value={invoiceType}
                        onChange={handleInvoiceTypeChange}
                        sx={{ gap: 1 }}
                      >
                        <FormControlLabel 
                          value="standard" 
                          control={<Radio size="small" sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#1976d2' } }} />} 
                          label={<Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: '#334155' }}>Standard CSV Template</Typography>} 
                        />
                        <FormControlLabel 
                          value="extended" 
                          control={<Radio size="small" sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#1976d2' } }} />} 
                          label={<Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: '#334155' }}>Extended CSV Layout</Typography>} 
                        />
                      </RadioGroup>
                    </Box>
                  </FormControl>
                </Grid>

                {/* 4. Submission Pipeline Operation Control Panel Trigger Footer */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pt: 2, borderTop: '1px solid #E2E8F0', mt: 1 }}>
                    <Button 
                      variant="contained" 
                      type="submit"
                      sx={{
                        textTransform: 'none',
                        borderRadius: '10px',
                        fontWeight: 700,
                        fontSize: '13.5px',
                        height: '38px',
                        px: 4,
                        boxShadow: 'none',
                        backgroundColor: '#1976d2', // --- CORPORATE IDENTITY BRAND ACCENT BLUE ---
                        color: '#FFFFFF',
                        '&:hover': { backgroundColor: '#115293', boxShadow: 'none' }
                      }}
                    >
                      Save Preferences
                    </Button>
                  </Box>
                </Grid>

              </Grid>
            </form>
            
            {/* Informational Lower Insight Panel */}
            <Box sx={{ mt: 3, display: 'flex', p: 1.5, backgroundColor: '#F8FAFC', borderRadius: '8px', border: '1px solid #F1F5F9' }}>
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500, fontSize: '12px', lineHeight: 1.5 }}>
                <span style={{ fontWeight: 700, color: '#475569' }}>Pro-Tip:</span> You can configure up to 10 separate target routing addresses inside each block parameter context by hitting the <kbd style={{ background: '#E2E8F0', padding: '1px 4px', borderRadius: '4px', fontFamily: 'monospace' }}>Enter</kbd> or <kbd style={{ background: '#E2E8F0', padding: '1px 4px', borderRadius: '4px', fontFamily: 'monospace' }}>,</kbd> key strings.
              </Typography>
            </Box>
          </Container>
        </CardContent>
      </Card>
    </Box>
  );
}

export default InvoiceSettingsPage;